import { z } from 'zod';
// Auth schemas
export const loginSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(1, 'Password is required'),
    totpCode: z.string().length(6).optional(),
});
export const registerSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(12, 'Password must be at least 12 characters'),
    displayName: z.string().min(1).max(100).optional(),
});
// VPS schemas
export const vpsProviderSchema = z.enum([
    'aws',
    'digitalocean',
    'hetzner',
    'linode',
    'vultr',
    'ovh',
    'contabo',
    'custom',
]);
export const vpsStatusSchema = z.enum([
    'online',
    'offline',
    'degraded',
    'unknown',
    'maintenance',
]);
// Helper to transform empty strings to null
const emptyToNull = z.string().transform((v) => (v === '' ? null : v));
const emptyToNullOptional = emptyToNull.nullish();
export const createVpsSchema = z.object({
    name: z.string().min(1).max(100),
    hostname: emptyToNullOptional.pipe(z.string().max(255).nullish()),
    provider: vpsProviderSchema.default('custom'),
    providerRef: emptyToNullOptional.pipe(z.string().max(100).nullish()),
    ipAddress: z.string().ip({ version: 'v4' }),
    ipv6Address: emptyToNullOptional.pipe(z.string().ip({ version: 'v6' }).nullish()),
    internalIp: emptyToNullOptional.pipe(z.string().ip({ version: 'v4' }).nullish()),
    sshPort: z.coerce.number().int().min(1).max(65535).default(22),
    sshUser: z.string().min(1).max(50).default('root'),
    sshKeyId: emptyToNullOptional.pipe(z.string().uuid().nullish()),
    cpuCores: z.coerce.number().int().positive().nullish().catch(null),
    ramMb: z.coerce.number().int().positive().nullish().catch(null),
    diskGb: z.coerce.number().int().positive().nullish().catch(null),
    osName: emptyToNullOptional.pipe(z.string().max(100).nullish()),
    osVersion: emptyToNullOptional.pipe(z.string().max(50).nullish()),
    purpose: emptyToNullOptional.pipe(z.string().max(255).nullish()),
    projects: z.array(z.string()).default([]),
    websites: z.array(z.string()).default([]), // Removed .url() requirement - can be any string
    notes: emptyToNullOptional.pipe(z.string().nullish()),
    tags: z.array(z.string()).default([]),
    sortOrder: z.coerce.number().int().default(0),
    isActive: z.boolean().default(true),
});
export const updateVpsSchema = createVpsSchema.partial();
// Metrics schemas
export const systemMetricsSchema = z.object({
    cpuPercent: z.number().min(0).max(100),
    cpuLoad1: z.number().min(0),
    cpuLoad5: z.number().min(0),
    cpuLoad15: z.number().min(0),
    ramUsedMb: z.number().int().min(0),
    ramTotalMb: z.number().int().positive(),
    ramPercent: z.number().min(0).max(100),
    swapUsedMb: z.number().int().min(0),
    swapTotalMb: z.number().int().min(0),
    diskUsedGb: z.number().min(0),
    diskTotalGb: z.number().positive(),
    diskPercent: z.number().min(0).max(100),
});
export const networkMetricsSchema = z.object({
    interface: z.string(),
    rxBytes: z.number().int().min(0),
    txBytes: z.number().int().min(0),
    rxPackets: z.number().int().min(0),
    txPackets: z.number().int().min(0),
    rxErrors: z.number().int().min(0),
    txErrors: z.number().int().min(0),
});
export const sshAttemptSchema = z.object({
    timestamp: z.string().datetime(),
    sourceIp: z.string().ip(),
    username: z.string().max(100),
    success: z.boolean(),
    authMethod: z.string().max(50).optional(),
});
export const metricsIngestSchema = z.object({
    timestamp: z.string().datetime(),
    system: systemMetricsSchema.optional(),
    network: z.array(networkMetricsSchema).optional(),
    sshAttempts: z.array(sshAttemptSchema).optional(),
});
// Query schemas
export const paginationSchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().positive().max(100).default(20),
});
export const timeRangeSchema = z.object({
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    range: z.enum(['1h', '6h', '24h', '7d', '30d']).optional(),
});
export const metricsQuerySchema = timeRangeSchema.extend({
    resolution: z.enum(['1m', '5m', '15m', '1h', '1d']).default('5m'),
});
// Port scan schemas
export const openPortSchema = z.object({
    port: z.number().int().min(1).max(65535),
    protocol: z.enum(['tcp', 'udp']),
    service: z.string().nullish(),
    version: z.string().nullish(),
});
export const portScanResultSchema = z.object({
    scanType: z.enum(['external', 'internal']),
    openPorts: z.array(openPortSchema),
    closedPorts: z.number().int().min(0),
    filteredPorts: z.number().int().min(0),
    riskLevel: z.enum(['low', 'medium', 'high', 'critical']),
});
// Profile & team schemas
export const updateProfileSchema = z.object({
    displayName: z.string().min(1).max(100).optional(),
    email: z.string().email('Invalid email address').optional(),
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(12, 'Password must be at least 12 characters').optional(),
});
export const createMemberSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(12, 'Password must be at least 12 characters'),
    displayName: z.string().min(1, 'Display name is required').max(100),
});
//# sourceMappingURL=schemas.js.map